import { useCallback, useEffect, useRef, useState } from "react";
import { searchLocationSuggestions } from "../services/location.service";

export type AwsLocationSuggestion = {
  PlaceId: string;
  Text: string;
  Category?: string;
};

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

/**
 * Debounced AWS Location (Place Index) suggestions for address search inputs.
 */
export function useAwsLocationAddressSearch() {
  const [suggestions, setSuggestions] = useState<AwsLocationSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelDismissSuggestions = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const scheduleDismissSuggestions = useCallback(() => {
    cancelDismissSuggestions();
    dismissTimerRef.current = setTimeout(() => {
      setShowSuggestions(false);
      dismissTimerRef.current = null;
    }, 200);
  }, [cancelDismissSuggestions]);

  const clearSuggestions = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    requestIdRef.current += 1;
    setSuggestions([]);
    setShowSuggestions(false);
    setLoading(false);
  }, []);

  const search = useCallback(
    (text: string) => {
      const query = text.trim();

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (query.length < MIN_QUERY_LENGTH) {
        clearSuggestions();
        return;
      }

      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;

      debounceTimerRef.current = setTimeout(async () => {
        try {
          setLoading(true);
          const results = await searchLocationSuggestions(query);
          if (currentRequestId !== requestIdRef.current) {
            return;
          }
          const normalized: AwsLocationSuggestion[] = (results || []).map(
            (item: { PlaceId?: string; Text?: string; Category?: string }) => ({
              PlaceId: String(item?.PlaceId ?? ""),
              Text: String(item?.Text ?? ""),
              Category:
                item?.Category != null ? String(item.Category) : undefined,
            })
          );
          setSuggestions(normalized);
          setShowSuggestions(normalized.length > 0);
        } catch (error) {
          if (currentRequestId !== requestIdRef.current) {
            return;
          }
          console.error("Location suggestion error", error);
          setSuggestions([]);
          setShowSuggestions(false);
        } finally {
          if (currentRequestId === requestIdRef.current) {
            setLoading(false);
          }
        }
      }, DEBOUNCE_MS);
    },
    [clearSuggestions]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      cancelDismissSuggestions();
    };
  }, [cancelDismissSuggestions]);

  return {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    loading,
    search,
    clearSuggestions,
    cancelDismissSuggestions,
    scheduleDismissSuggestions,
  };
}
